import { z } from "zod";
import { zId, zIsoDate, zMoney, zQueryBoolean } from "./common.js";

// ---- gift ask pipeline (CT-FEAT-005) ----

export const ASK_STATUSES = ["planned", "asked", "verbal", "committed", "won", "lost"] as const;
export const zAskStatus = z.enum(ASK_STATUSES);
export type AskStatus = z.infer<typeof zAskStatus>;

export const ASK_STATUS_LABELS: Record<AskStatus, string> = {
  planned: "Planned",
  asked: "Asked",
  verbal: "Verbal yes",
  committed: "Committed",
  won: "Won",
  lost: "Lost",
};

/** Default close probability (%) applied when an ask enters a status without an explicit probability. */
export const ASK_DEFAULT_PROBABILITY: Record<AskStatus, number> = {
  planned: 10,
  asked: 30,
  verbal: 60,
  committed: 90,
  won: 100,
  lost: 0,
};

export const OPEN_ASK_STATUSES: readonly AskStatus[] = ["planned", "asked", "verbal", "committed"];

/** Probability-weighted value in integer cents (rounded half-up per ask). */
export function weightedAmount(amount: number, probability: number): number {
  return Math.round((amount * probability) / 100);
}

export const zAskProbability = z.number().int().min(0).max(100);

export interface AskStatusChange {
  at: string;
  from: AskStatus | null;
  to: AskStatus;
  by: string | null;
}

export const zGiftAsk = z.object({
  id: zId,
  constituentId: zId,
  constituentName: z.string().nullable(),
  portfolioAssignmentId: zId.nullable(),
  ownerId: zId,
  ownerName: z.string().nullable(),
  name: z.string(),
  amount: zMoney,
  weightedAmount: zMoney,
  fundId: zId.nullable(),
  fundName: z.string().nullable(),
  campaignId: zId.nullable(),
  campaignName: z.string().nullable(),
  status: zAskStatus,
  probability: zAskProbability,
  expectedClose: zIsoDate.nullable(),
  askedOn: zIsoDate.nullable(),
  closedOn: zIsoDate.nullable(),
  lostReason: z.string().nullable(),
  pledgeId: zId.nullable(),
  giftId: zId.nullable(),
  notes: z.string().nullable(),
  statusHistory: z.array(z.object({ at: z.string(), from: zAskStatus.nullable(), to: zAskStatus, by: z.string().nullable() })),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GiftAsk = z.infer<typeof zGiftAsk>;

export const zCreateAsk = z.object({
  /** Must be a live constituent in the caller's org. */
  constituentId: zId,
  name: z.string().trim().min(1).max(200),
  amount: zMoney,
  /** Omitted → the creator. Must be an active user in the caller's org. */
  ownerId: zId.optional(),
  fundId: zId.nullable().optional(),
  campaignId: zId.nullable().optional(),
  /** Omitted → the donor's Moves assignment for the owner, if any. */
  portfolioAssignmentId: zId.nullable().optional(),
  status: zAskStatus.default("planned"),
  probability: zAskProbability.optional(),
  expectedClose: zIsoDate.nullable().optional(),
  askedOn: zIsoDate.nullable().optional(),
  notes: z.string().max(10_000).nullable().optional(),
  lostReason: z.string().trim().min(1).max(1000).optional(),
  pledgeId: zId.optional(),
  giftId: zId.optional(),
});
export type CreateAsk = z.input<typeof zCreateAsk>;

export const zUpdateAsk = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  amount: zMoney.optional(),
  ownerId: zId.optional(),
  fundId: zId.nullable().optional(),
  campaignId: zId.nullable().optional(),
  portfolioAssignmentId: zId.nullable().optional(),
  /** Moving to `won` needs a pledgeId or giftId (same donor); `lost` needs lostReason. */
  status: zAskStatus.optional(),
  /** Omitted on a status change → that status's default probability. */
  probability: zAskProbability.optional(),
  expectedClose: zIsoDate.nullable().optional(),
  askedOn: zIsoDate.nullable().optional(),
  closedOn: zIsoDate.nullable().optional(),
  lostReason: z.string().trim().min(1).max(1000).nullable().optional(),
  pledgeId: zId.nullable().optional(),
  giftId: zId.nullable().optional(),
  notes: z.string().max(10_000).nullable().optional(),
});
export type UpdateAsk = z.input<typeof zUpdateAsk>;

export const zAskListQuery = z.object({
  ownerId: zId.optional(),
  mine: zQueryBoolean.optional(),
  /** single status or comma-separated list */
  status: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(",").filter(Boolean) : undefined))
    .pipe(z.array(zAskStatus).optional()),
  /** open = planned/asked/verbal/committed */
  open: zQueryBoolean.optional(),
  constituentId: zId.optional(),
  campaignId: zId.optional(),
  closeFrom: zIsoDate.optional(),
  closeTo: zIsoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(500),
});
export type AskListQuery = z.input<typeof zAskListQuery>;

export interface AskBucket {
  count: number;
  amount: number;
  weighted: number;
}
export interface AskTotals extends AskBucket {
  byStatus: Record<AskStatus, AskBucket>;
}
export interface AskListResult {
  items: GiftAsk[];
  totals: AskTotals;
}
